import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Assessment as AssessmentIcon,
  Pending as PendingIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../../lib/axios";
import { NavContext } from "../../Pages/ServiceProvDash/ServiceProvDrawer";
// import { NavContext } from "./ServiceProvDrawer";

const StyledCard = styled(motion(Card), {
  shouldForwardProp: (prop) => prop !== "isTaskCard",
})(({ theme, isTaskCard }) => ({
  borderRadius: "15px",
  background: "#FFFFFF",
  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
  border: "1px solid #E5E7EB",
  display: "flex",
  flexDirection: "column",
  alignItems: isTaskCard ? "flex-start" : "center",
  justifyContent: "center",
  cursor: isTaskCard ? "default" : "pointer",
  transition: "all 0.2s ease",
  position: "relative",
  overflow: "hidden",
  "&:hover": !isTaskCard && {
    boxShadow: "0 4px 8px rgba(25, 118, 210, 0.15)",
    transform: "translateY(-2px) scale(1.02)",
    borderColor: "#1976D2",
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 50,
  height: 50,
  background: "#E3F2FD",
  color: "#1976D2",
  marginBottom: theme.spacing(1),
  border: "1px solid #E5E7EB",
}));

const getStatusColor = (status) => {
  switch (status) {
    case "Completed":
      return "success";
    case "Pending":
      return "error";
    case "Inprogress":
      return "warning";
    default:
      return "default";
  }
};

const DashboardPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { setNavValue } = useContext(NavContext);

  const [dashboardData, setDashboardData] = useState({
    metrics: [],
    tasks: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.post(
          "TrnTicketing/GetTrnTicketDashBoard",
          {
            start: 0,
            PageSize: 0,
            SearchText: "",
          }
        );

        if (response.data.Status === 0 && response.data.Data) {
          const tasks = response.data.Data.map((item) => {
            const ticketDate = new Date(item.TktDate);
            return {
              id: item.TicketId,
              ticketNo: item.TicketNo,
              title: item.ServiceName,
              status: item.Status === "1" ? "Completed" : item.Status === "O" ? "Pending" : "Inprogress",
              assignedBy: item.Warden || "Unknown",
              date: ticketDate.toISOString().split("T")[0],
              time: ticketDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              category: item.CategoryName || "N/A",
              subCategory: item.SubCategoryName || "N/A",
            };
          });

          const totalTasks = tasks.length;
          const completedTasks = tasks.filter((task) => task.status === "Completed").length;
          const inProgressTasks = tasks.filter((task) => task.status === "Inprogress").length;
          const pendingTasks = tasks.filter((task) => task.status === "Pending").length;

          const metrics = [
            {
              label: "Total Tasks",
              value: totalTasks,
              icon: <AssignmentTurnedInIcon sx={{ fontSize: 32 }} />,
              index: 0,
            },
            {
              label: "Completed",
              value: completedTasks,
              icon: <CheckCircleIcon sx={{ fontSize: 32 }} />,
              index: 1,
            },
            {
              label: "Inprogress",
              value: inProgressTasks,
              icon: <PendingIcon sx={{ fontSize: 32 }} />,
              index: 2,
            },
            {
              label: "Pending",
              value: pendingTasks,
              icon: <AssessmentIcon sx={{ fontSize: 32 }} />,
              index: 3,
            },
          ];

          setDashboardData({ metrics, tasks });
        } else {
          setError("Failed to fetch data: " + response.data.Message);
        }
      } catch (err) {
        setError("Error fetching data: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  useEffect(() => {
    const currentIndex = dashboardData.metrics.findIndex(
      (metric) => metric.route === location.pathname
    );
    if (currentIndex !== -1 && typeof setNavValue === "function") {
      setNavValue(currentIndex);
    }
  }, [location.pathname, setNavValue, dashboardData.metrics]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          color: "error.main",
        }}
      >
        <Typography variant="h6" component="div">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F9FAFB",
        background: "linear-gradient(180deg, #F9FAFB, #E5E7EB)",
        overflow: "auto",
        display: { xs: "flex", sm: "none", md: "none" },
        flexDirection: "column",
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 },
        mb: "60px",
      }}
    >
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1.5rem", sm: "1.6rem" },
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontFamily: '"Roboto", sans-serif',
            letterSpacing: "0.01em",
          }}
          component={motion.div}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          Service Dashboard
        </Typography>
        <Typography
          variant="body2"
          component="div"
          sx={{
            color: "#6B7280",
            fontFamily: '"Roboto", sans-serif',
            fontSize: "0.9rem",
            mt: 0.5,
            lineHeight: 1.4,
          }}
        >
          Manage and track your service tasks
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontWeight: 600,
            mb: 1,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontFamily: '"Roboto", sans-serif',
            textAlign: "center",
            fontSize: { xs: "1.3rem", sm: "1.3rem" },
          }}
        >
          Task Overview
        </Typography>
        <Grid container spacing={2} justifyContent="center">
          {dashboardData.metrics.map((metric, index) => (
            <Grid item key={index} xs={6} sm={2.5} md={2}>
              <StyledCard
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                isTaskCard={false}
                onClick={() => {
                  if (metric.route) {
                    navigate(metric.route);
                    if (typeof setNavValue === "function") {
                      setNavValue(index);
                    }
                  }
                }}
                aria-label={`Navigate to ${metric.label}`}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 1,
                    px: 1.25,
                    color: "#111827",
                    textAlign: "center",
                  }}
                >
                  <StyledAvatar>{metric.icon}</StyledAvatar>
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      fontWeight: 600,
                      fontSize: "1.2rem",
                      color: "#111827",
                      maxWidth: "100%",
                      lineHeight: 1.3,
                    }}
                  >
                    {metric.label}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={metric.value}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: "1.2rem",
                        bgcolor: "#FFFFFF",
                      }}
                    />
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box>
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontWeight: 600,
            mb: 2,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontFamily: '"Roboto", sans-serif',
            textAlign: "center",
            fontSize: { xs: "1.3rem", sm: "1.3rem" },
          }}
        >
          Assigned Tasks
        </Typography>
        <Grid container spacing={2} justifyContent="center">
          {dashboardData.tasks.map((task, index) => (
            <Grid item xs={12} sm={6} md={4} key={task.id}>
              <StyledCard
                index={index}
                isTaskCard={true}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <CardContent
                  sx={{
                    py: 1.5,
                    px: 2,
                    color: "#111827",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "nowrap",
                      gap: 14,
                    }}
                  >
                    <Typography
                      variant="body1"
                      component="div"
                      sx={{
                        fontFamily: '"Roboto", sans-serif',
                        color: "#111827",
                        fontSize: "1rem",
                        fontWeight: 600,
                      }}
                    >
                      TicketNo- {task.ticketNo}
                    </Typography>
                  
                  </Box>
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      fontFamily: '"Roboto", sans-serif',
                      color: "#111827",
                      mt: 0.5,
                      fontSize: "0.9rem",
                      whiteSpace: "normal",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.3,
                    }}
                  >
                    Ticket For: {task.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      fontFamily: '"Roboto", sans-serif',
                      color: "#6B7280",
                      mt: 0.5,
                      fontSize: "0.9rem",
                    }}
                  >
                    Category: {task.category}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      fontFamily: '"Roboto", sans-serif',
                      color: "#6B7280",
                      mt: 0.5,
                      fontSize: "0.9rem",
                    }}
                  >
                    Subcategory: {task.subCategory}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      fontFamily: '"Roboto", sans-serif',
                      color: "#6B7280",
                      mt: 0.5,
                      fontSize: "0.9rem",
                    }}
                  >
                    Assigned By: {task.assignedBy}
                  </Typography>
                    <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      fontFamily: '"Roboto", sans-serif',
                      color: "#6B7280",
                      mt: 0.5,
                      fontSize: "0.9rem",
                    }}
                  >
                        Date: {task.date} | Time: {task.time}
                  </Typography>
                    <Chip
                      label={task.status}
                      color={getStatusColor(task.status)}
                      size="small"
                      sx={{ fontWeight: 600, fontSize: "0.95rem" }}
                    />
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default DashboardPage;